import { motion } from 'framer-motion';
import { Clock, Users, ArrowRight } from 'lucide-react';

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const BADGE_STYLES = {
  green: 'bg-emerald-500/15 text-emerald-400',
  indigo: 'bg-[#8899ff]/15 text-[#8899ff]',
  'gold-filled': 'bg-primary text-primary-foreground',
  gold: 'bg-primary/15 text-primary',
};

const ACCENT_CONFIG = {
  gold: {
    iconBg: 'bg-primary/15',
    iconColor: 'text-primary',
    priceColor: 'text-primary',
    bulletDot: 'bg-primary',
    ctaBg: 'bg-primary hover:bg-primary/80 shadow-[0_3px_0_0_rgba(168,130,20,1)] hover:shadow-[0_1px_0_0_rgba(168,130,20,1)]',
  },
  indigo: {
    iconBg: 'bg-[#8899ff]/15',
    iconColor: 'text-[#8899ff]',
    priceColor: 'text-[#8899ff]',
    bulletDot: 'bg-[#8899ff]',
    ctaBg: 'bg-[#8899ff] hover:bg-[#7788ee] shadow-[0_3px_0_0_rgba(100,120,200,1)] hover:shadow-[0_1px_0_0_rgba(100,120,200,1)]',
  },
};

function BadgeGroup({ badges }) {
  if (!badges?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {badges.map((b) => (
        <span key={b.label} className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${BADGE_STYLES[b.type] || BADGE_STYLES.gold}`}>
          {b.label}
        </span>
      ))}
    </div>
  );
}

function MetaRow({ price, duration, groupSize, priceColor }) {
  if (!price && !duration && !groupSize) return null;
  return (
    <div className="flex flex-wrap items-center gap-4 mb-3 text-sm">
      {price && <span className={`font-semibold ${priceColor}`}>{price}</span>}
      {duration && (
        <span className="flex items-center gap-1 text-slate-400">
          <Clock className="w-3.5 h-3.5" /> {duration}
        </span>
      )}
      {groupSize && (
        <span className="flex items-center gap-1 text-slate-400">
          <Users className="w-3.5 h-3.5" /> {groupSize}
        </span>
      )}
    </div>
  );
}

function BulletList({ items, dotClass }) {
  if (!items?.length) return null;
  return (
    <ul className="mt-3 space-y-1.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2 text-sm text-slate-300">
          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} />
          {item}
        </li>
      ))}
    </ul>
  );
}

function CtaButton({ href, label, ctaBg, testId }) {
  return (
    <a
      href={href}
      className={`inline-flex items-center gap-2 mt-4 text-white hover:text-white px-5 py-2 rounded-lg text-sm font-semibold hover:translate-y-[2px] active:shadow-none active:translate-y-[3px] transition-all duration-100 ${ctaBg}`}
      data-testid={testId}
    >
      {label} <ArrowRight className="w-4 h-4" />
    </a>
  );
}

export function ServiceCard({ card, featured, accentColor = 'gold' }) {
  const accent = ACCENT_CONFIG[accentColor];
  const Icon = card.icon;

  return (
    <motion.div
      className={`rounded-xl border p-6 ${
        featured
          ? 'border-primary/40 bg-gradient-to-br from-primary/5 via-card to-card shadow-lg shadow-primary/5'
          : 'border-border/30 bg-card'
      }`}
      {...fade}
      data-testid={`card-${card.id}`}
    >
      <div className="flex items-start gap-4 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${accent.iconBg}`}>
          <Icon className={`w-5 h-5 ${accent.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-white mb-1">{card.title}</h3>
          <BadgeGroup badges={card.badges} />
        </div>
      </div>

      <MetaRow price={card.price} duration={card.duration} groupSize={card.groupSize} priceColor={accent.priceColor} />

      {card.subheading && <p className="text-sm text-slate-300 font-medium mb-2">{card.subheading}</p>}
      {card.description && <p className="text-sm text-slate-400 leading-relaxed">{card.description}</p>}
      {card.note && <p className="text-xs text-slate-500 italic mt-2">{card.note}</p>}

      {card.includes && (
        <div className="mt-3">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Includes</span>
          <BulletList items={card.includes} dotClass={accent.bulletDot} />
        </div>
      )}

      <BulletList items={card.bullets} dotClass="bg-[#8899ff]" />

      <CtaButton href={card.ctaLink} label={card.ctaLabel} ctaBg={accent.ctaBg} testId={`cta-${card.id}`} />
    </motion.div>
  );
}
